import React from 'react';
import { theme } from '../theme';
import type { FrameworkMode, FrameworkModeDef } from '../types';
import { useLanguage } from '../i18n';

interface ProfileSelectorProps {
  /** Currently active framework mode */
  activeMode: FrameworkMode;
  /** Available framework modes from sovereignty_profiles.json */
  frameworkModes: Record<string, FrameworkModeDef>;
  /** Callback when the user selects a different mode */
  onChange: (mode: FrameworkMode) => void;
}

/**
 * Segmented control to switch between the 3 framework modes:
 * NO_FRAMEWORK_C5, EU-CSF, C3A
 */
export const ProfileSelector: React.FC<ProfileSelectorProps> = ({
  activeMode,
  frameworkModes,
  onChange,
}) => {
  const { language } = useLanguage();
  const modeIds = Object.keys(frameworkModes) as FrameworkMode[];

  return (
    <div>
      <div
        style={{
          display: 'inline-flex',
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${theme.border}`,
        }}
      >
        {modeIds.map((id, idx) => {
          const isActive = id === activeMode;
          const mode = frameworkModes[id];
          const label = language === 'en' && mode?.labelEn ? mode.labelEn : mode?.label ?? id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              title={language === 'en' && mode?.descriptionEn ? mode.descriptionEn : mode?.description ?? ''}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                border: 'none',
                borderRight: idx < modeIds.length - 1 ? `1px solid ${theme.border}` : 'none',
                background: isActive ? theme.secondary : '#fff',
                color: isActive ? '#fff' : theme.text,
                transition: 'background 0.2s, color 0.2s',
              }}
              aria-pressed={isActive}
            >
              {label}
            </button>
          );
        })}
      </div>
      {/* Description below the selector */}
      {frameworkModes[activeMode] && (
        <div style={{ fontSize: 11, color: theme.muted, marginTop: 4, maxWidth: 600 }}>
          {language === 'en' && frameworkModes[activeMode].descriptionEn
            ? frameworkModes[activeMode].descriptionEn
            : frameworkModes[activeMode].description}
        </div>
      )}
    </div>
  );
};
